import { useState } from 'react'
import { useTimelineStore, selectTimeline } from '@/lib/store/timeline-store'
import { useAuthStore } from '@/lib/store/auth-store'

interface HermesPanelProps {
  roomId: string
  roomName: string
  onClose: () => void
}

type Action = 'summarize' | 'translate' | 'draft' | 'ask'

// Hermes: a private, on-demand assistant scoped to THIS room. Nothing runs until
// you click; results are shown only to you and never posted anywhere.
export function HermesPanel({ roomId, roomName, onClose }: HermesPanelProps) {
  const timeline = useTimelineStore(selectTimeline(roomId))
  const userId = useAuthStore((s) => s.userId)
  const [result, setResult] = useState('')
  const [lastAction, setLastAction] = useState<Action | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [copied, setCopied] = useState(false)

  const run = async (action: Action, q?: string) => {
    if (loading) return
    setLoading(true)
    setError(null)
    setResult('')
    setLastAction(action)
    const messages = timeline
      .filter((e) => (e.type === 'm.room.message' || e.type === 'm.sticker') && !e.redacted && e.content.body)
      .slice(-40)
      .map((e) => ({ sender: e.senderName, body: e.content.body as string }))
    try {
      const res = await fetch('/api/hermes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: userId, action, query: q, roomName, messages }),
      })
      const data = (await res.json()) as { text?: string; error?: string }
      if (data.error === 'no_api_key') setError('AI isn’t configured on the server.')
      else if (data.error === 'not_allowed') setError('Your account isn’t allowed to use AI features.')
      else if (data.error) setError('Hermes failed — try again.')
      else setResult(data.text ?? '')
    } catch {
      setError('Couldn’t reach Hermes.')
    } finally {
      setLoading(false)
    }
  }

  const copy = () => {
    navigator.clipboard.writeText(result).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <aside className="members-panel">
      <div className="members-head">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-[rgb(var(--np-tx-primary))]">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgb(var(--np-accent))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
          </svg>
          Hermes
        </span>
        <button onClick={onClose} className="icon-btn-sm" title="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>

      <div className="scroll flex-1 overflow-y-auto p-3 space-y-3">
        <p className="text-[11px] text-[rgb(var(--np-tx-muted))] leading-relaxed">
          Private to you — scoped to <span className="font-medium text-[rgb(var(--np-tx-secondary))]">{roomName}</span>. Nothing is posted to the chat.
        </p>

        <div className="grid grid-cols-2 gap-1.5">
          <HermesButton label="Summarize" onClick={() => run('summarize')} disabled={loading} />
          <HermesButton label="Translate" onClick={() => run('translate')} disabled={loading} />
          <HermesButton label="Draft reply" onClick={() => run('draft', query.trim() || undefined)} disabled={loading} />
          <HermesButton label="Ask" onClick={() => query.trim() && run('ask', query.trim())} disabled={loading || !query.trim()} />
        </div>

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && query.trim()) run('ask', query.trim()) }}
          placeholder="Ask about this conversation…"
          className="w-full bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2 text-sm text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)]"
        />

        {loading && <p className="text-xs text-[rgb(var(--np-tx-muted))] italic">Hermes is thinking…</p>}
        {error && <p className="text-xs text-[rgb(var(--np-status-critical))]">{error}</p>}
        {result && (
          <div className="rounded-md border border-[rgb(var(--np-border))] bg-[rgb(var(--np-raised)/0.5)] p-3">
            <p className="text-sm text-[rgb(var(--np-tx-secondary))] whitespace-pre-wrap break-words">{result}</p>
            <button
              onClick={copy}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[rgb(var(--np-accent))] hover:underline"
            >
              {copied ? 'Copied!' : lastAction === 'draft' ? 'Copy draft' : 'Copy'}
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

function HermesButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-2.5 py-2 text-xs font-medium rounded-md border border-[rgb(var(--np-border))] text-[rgb(var(--np-tx-secondary))] hover:bg-[rgb(var(--np-raised))] hover:border-[rgb(var(--np-border-bright))] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  )
}
